import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type {
  AccountsAccountUpdateInput,
  AccountsSessionDurationRequest,
  AccountsSignupRequest,
  AccountsSignupResponse,
  AccountsSignLogoRequest,
  AccountsSignLogoResponse,
  AccountsUpdateAddressRequest,
} from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import { deleteCrossAppCacheByPrefix, deleteCrossAppCacheKeys } from '@/utils/cross-app-cache';
import type { GCSUploadOptions, S3UploadOptions, SignResponse } from '@/services/uploads';
import type { HookMutationConfig } from '../shared/react-query-types';
import { accountKeys, parseAccount, partnerAccountsQueryKey, type Account } from './entity';

export type { AccountsAccountUpdateInput, AccountsSignupRequest, AccountsSignupResponse };

export type UpdateAccountInput = AccountsAccountUpdateInput & { accountId: number };

/** @public Persist account settings (name, emails, address, logo). */
export async function updateAccount({ accountId, ...account }: UpdateAccountInput) {
  return hydraUiApi.accounts.update({
    params: { path: { account_id: String(accountId) } },
    body: { account },
  });
}

/** @public Request a signed upload policy for the account logo. */
export async function signAccountLogo(
  accountId: number,
  file: AccountsSignLogoRequest,
): Promise<AccountsSignLogoResponse> {
  return hydraUiApi.accounts.signLogo({
    params: { path: { account_id: String(accountId) } },
    body: file,
  });
}

/**
 * Narrow the generated sign-logo response (whose `gcs`/`s3` policies are
 * nullable) into the `SignResponse` the upload helpers expect.
 */
export function toSignResponse(response: AccountsSignLogoResponse): SignResponse {
  // The generated policy types are sparsely nullable; the upload helpers expect
  // the concrete option shapes. The backend always returns a complete policy.
  if (response.gcs != null) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return { gcs: response.gcs as GCSUploadOptions };
  }
  if (response.s3 != null) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return { s3: response.s3 as S3UploadOptions };
  }
  throw new Error('Sign logo response did not contain a GCS or S3 policy');
}

export type UpdateAccountAddressInput = AccountsUpdateAddressRequest & { accountId: number };

/** @public Update the address saved to the account. Returns the updated account. */
export async function updateAccountAddress({ accountId, ...address }: UpdateAccountAddressInput) {
  return hydraUiApi.accounts.address({
    params: { path: { account_id: String(accountId) } },
    body: address,
  });
}

/**
 * Refresh the shell's cached account after a save — the Hydra-native equivalent
 * of the Ember `announceRefreshAuth()` handoff.
 *
 * The cross-app account payloads are dropped first, for the reason
 * `invalidateAuthenticatedUser` documents: the `['authenticated']` query fn is
 * `fetchCurrentAccount()`, which answers from that cache before it reaches the
 * network, so the invalidation alone is a no-op for up to ~10s and repopulates
 * the store from the pre-save snapshot. Surfaces that read the account from the
 * auth store — the billing contact card among them — would otherwise keep
 * showing the old values until a reload, behind a success snackbar.
 */
function refreshAuthenticatedAccount(queryClient: QueryClient, accountId: number): void {
  // Exact keys, not a `/accounts` prefix bust: that prefix also covers the billing and
  // role-definition caches, which an account save has no reason to discard.
  deleteCrossAppCacheKeys('/accounts/current', `/accounts/${accountId}`);
  deleteCrossAppCacheByPrefix('/account_by_environment');
  void queryClient.invalidateQueries({ queryKey: ['authenticated'] });
}

/** Save the account. On success it also replaces the cached detail record. */
interface UseUpdateAccountOptions {
  accountId: number;
  mutationConfig?: HookMutationConfig<Awaited<ReturnType<typeof updateAccount>>, AccountsAccountUpdateInput>;
}

export function useUpdateAccount({ accountId, mutationConfig }: UseUpdateAccountOptions) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    ...rest,
    mutationFn: (input: AccountsAccountUpdateInput) => updateAccount({ accountId, ...input }),
    onSuccess: (response, ...callbackArgs) => {
      if (response.account != null) {
        queryClient.setQueryData(accountKeys.detail(accountId, String(accountId)), parseAccount(response.account));
      }
      refreshAuthenticatedAccount(queryClient, accountId);
      onSuccess?.(response, ...callbackArgs);
    },
  });
}

interface UseUpdateAccountAddressOptions {
  accountId: number;
  mutationConfig?: HookMutationConfig<Awaited<ReturnType<typeof updateAccountAddress>>, AccountsUpdateAddressRequest>;
}

/**
 * @public Save the billing address on the account. The endpoint returns the
 * fresh account, so on success it replaces the cached detail record and
 * invalidates `['authenticated']` — the shell's cached account holds the same
 * address fields the billing forms read.
 */
export function useUpdateAccountAddress({ accountId, mutationConfig }: UseUpdateAccountAddressOptions) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    ...rest,
    mutationFn: (address: AccountsUpdateAddressRequest) => updateAccountAddress({ accountId, ...address }),
    onSuccess: (response, ...callbackArgs) => {
      if (response.account != null) {
        queryClient.setQueryData(accountKeys.detail(accountId, String(accountId)), parseAccount(response.account));
      }
      refreshAuthenticatedAccount(queryClient, accountId);
      onSuccess?.(response, ...callbackArgs);
    },
  });
}

/**
 * @public Provision a new account on behalf of the partner. The backend creates
 * the client account + owner and grants the calling partner (`accountId` = the
 * partner's own account) cross-org membership in it. Gated server-side by the
 * account's `partner_account` attribute + account-edit permission.
 */
export async function createPartnerAccount(accountId: number, input: AccountsSignupRequest) {
  return hydraUiApi.accounts.signup({
    params: { path: { account_id: String(accountId) } },
    body: input,
  });
}

interface UseCreatePartnerAccountOptions {
  accountId: number;
  mutationConfig?: HookMutationConfig<Awaited<ReturnType<typeof createPartnerAccount>>, AccountsSignupRequest>;
}

export function useCreatePartnerAccount({ accountId, mutationConfig }: UseCreatePartnerAccountOptions) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    ...rest,
    mutationFn: (input: AccountsSignupRequest) => createPartnerAccount(accountId, input),
    onSuccess: (response, ...callbackArgs) => {
      // The partner is now a member of a new account — refresh the shell's
      // authenticated state and the membership list so the account switcher
      // (useAccountMemberships -> getMemberships, a separate cache) reflects the
      // new account immediately rather than after its 5-minute refetch.
      void queryClient.invalidateQueries({ queryKey: ['authenticated'] });
      void queryClient.invalidateQueries({ queryKey: ['getMemberships'] });
      // Refresh the partner accounts list so the newly created account shows up
      // when the user is returned to it.
      void queryClient.invalidateQueries({ queryKey: partnerAccountsQueryKey(accountId) });
      onSuccess?.(response, ...callbackArgs);
    },
  });
}

/* ------------------------------------------------------------------------- */
/* Security settings: session duration + 2FA requirement                      */
/* ------------------------------------------------------------------------- */

// Body field (`session_duration`, seconds) derived from the generated request type.
export interface UpdateSessionDurationInput extends AccountsSessionDurationRequest {
  accountId: number;
}

/** @public Set the account's login session duration (seconds). Returns 204. */
export async function updateSessionDuration({
  accountId,
  session_duration,
}: UpdateSessionDurationInput): Promise<void> {
  await hydraUiApi.accounts.sessionDuration({
    params: { path: { account_id: String(accountId) } },
    body: { session_duration },
  });
}

interface UseUpdateSessionDurationOptions {
  accountId: number;
  mutationConfig?: HookMutationConfig<void, number>;
}

/**
 * @public Save the login session duration. The endpoint returns no body, so on
 * success the account's `session_duration` is patched into the cached record
 * (the Ember model pushed the same delta into the store).
 */
export function useUpdateSessionDuration({ accountId, mutationConfig }: UseUpdateSessionDurationOptions) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (session_duration: number) => updateSessionDuration({ accountId, session_duration }),
    onSuccess: (result, session_duration, ...callbackArgs) => {
      queryClient.setQueryData<Account>(accountKeys.detail(accountId, String(accountId)), (prev) =>
        prev == null ? prev : { ...prev, session_duration },
      );
      onSuccess?.(result, session_duration, ...callbackArgs);
    },
  });
}

export type TwoFactorAuthRequiredAction = 'enable' | 'disable';

export interface SetTwoFactorAuthRequiredInput {
  accountId: number;
  action: TwoFactorAuthRequiredAction;
}

/** @public Enable or disable the account-wide 2FA requirement. Returns 204. */
export async function setTwoFactorAuthRequired({ accountId, action }: SetTwoFactorAuthRequiredInput): Promise<void> {
  await hydraUiApi.accounts._2faRequired({
    params: { path: { account_id: String(accountId), action } },
  });
}

interface UseSetTwoFactorAuthRequiredOptions {
  accountId: number;
  mutationConfig?: HookMutationConfig<void, TwoFactorAuthRequiredAction>;
}

/**
 * @public Toggle the account-wide 2FA requirement. The endpoint returns no
 * body, so on success the account record and the shell's authenticated state
 * are invalidated to refetch the updated `two_factor_authentication_required`
 * flag (the Ember components reloaded the account for the same reason).
 *
 * The flag is patched into the cached account synchronously — the outcome is
 * known from `action`, and the backend rejects every other outcome before
 * committing — so the panel's Enabled/Recommended label flips with the success
 * snackbar rather than lagging behind a refetch (Ember awaited its account
 * reload before creating the snackbar).
 *
 * The invalidations deliberately use `refetchType: 'none'`: they mark the
 * account stale for the next mount but do not re-read it now. The account GET is
 * replica-routed (`ui_api/accounts/dom.go` reads via `GlobalOrReplicaTransact1`)
 * and the EU datacenter serves it from a replica with up to 30s of lag
 * (`db/replication.go`, `MAX_REPLICATION_LAG = 30`), so an immediate refetch can
 * answer with the pre-write value and revert the patch — flipping the label back
 * to "Recommended" right after the success toast.
 */
export function useSetTwoFactorAuthRequired({ accountId, mutationConfig }: UseSetTwoFactorAuthRequiredOptions) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};
  return useMutation({
    ...rest,
    mutationFn: (action: TwoFactorAuthRequiredAction) => setTwoFactorAuthRequired({ accountId, action }),
    onSuccess: (result, action, ...callbackArgs) => {
      queryClient.setQueryData<Account>(accountKeys.detail(accountId, String(accountId)), (prev) =>
        prev == null ? prev : { ...prev, two_factor_authentication_required: action === 'enable' },
      );
      void queryClient.invalidateQueries({
        queryKey: accountKeys.detail(accountId, String(accountId)),
        refetchType: 'none',
      });
      void queryClient.invalidateQueries({ queryKey: ['authenticated'], refetchType: 'none' });
      onSuccess?.(result, action, ...callbackArgs);
    },
  });
}
